import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, X, MessageSquare, Calendar, Sparkles } from "lucide-react";
import api from "@/lib/api";

const ICONS = {
  coach_reply: MessageSquare,
  booking_confirmed: Calendar,
  booking_cancelled: Calendar,
  deal_status: Sparkles,
  system: Sparkles,
};

function relTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const sec = Math.max(1, Math.round((Date.now() - then) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.round(hr / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const fetchCount = async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setCount(data.count || 0);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    fetchCount();
    const i = setInterval(fetchCount, 30000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/notifications", { params: { limit: 20 } })
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      setItems((arr) => arr.map((n) => ({ ...n, read: true })));
      setCount(0);
    } catch (e) { /* ignore */ }
  };

  const clearAll = async () => {
    try {
      await api.delete("/notifications");
      setItems([]);
      setCount(0);
    } catch (e) { /* ignore */ }
  };

  const dismiss = async (n) => {
    try { await api.delete(`/notifications/${n.id}`); } catch (e) { /* ignore */ }
    setItems((arr) => arr.filter((x) => x.id !== n.id));
    if (!n.read) setCount((c) => Math.max(0, c - 1));
  };

  const handleClick = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch (e) { /* ignore */ }
      setItems((arr) => arr.map((x) => x.id === n.id ? { ...x, read: true } : x));
      setCount((c) => Math.max(0, c - 1));
    }
    setOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded transition hover:bg-white/5"
        style={{ color: "var(--text-muted)" }}
        data-testid="notifications-bell"
        title="Notifications"
      >
        <Bell size={16} />
        {count > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full font-mono text-[10px] flex items-center justify-center"
            style={{ background: "var(--lime)", color: "#161616" }}
            data-testid="notifications-bell-count"
          >
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-96 max-w-[90vw] rounded-xl z-50 overflow-hidden"
          style={{ background: "var(--surface)", border: "1px solid var(--border)", boxShadow: "0 8px 24px rgba(0,0,0,0.3)" }}
          data-testid="notifications-dropdown"
        >
          <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: "var(--border)" }}>
            <span className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--lime)" }}>Notifications</span>
            <div className="flex items-center gap-3">
              {count > 0 && (
                <button onClick={markAllRead} className="text-xs font-mono" style={{ color: "var(--text-muted)" }} data-testid="mark-all-read">
                  Mark all read
                </button>
              )}
              {items.length > 0 && (
                <button onClick={clearAll} className="text-xs font-mono" style={{ color: "var(--text-muted)" }} data-testid="clear-all-notifications">
                  Clear all
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-xs"><X size={14} /></button>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-8 flex justify-center"><span className="spinner" /></div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center text-sm" style={{ color: "var(--text-muted)" }}>You're all caught up.</div>
            ) : (
              items.map((n) => {
                const Icon = ICONS[n.type] || Sparkles;
                const inner = (
                  <div
                    className="flex items-start gap-3 p-4 pr-10 transition hover:bg-white/[0.03] border-b"
                    style={{
                      borderColor: "var(--border)",
                      background: n.read ? "transparent" : "var(--lime-glow)",
                    }}
                  >
                    <Icon size={16} className="flex-shrink-0 mt-0.5" style={{ color: n.read ? "var(--text-muted)" : "var(--lime)" }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium leading-snug">{n.title}</div>
                      {n.body && (
                        <div className="text-xs mt-1 truncate" style={{ color: "var(--text-muted)" }}>{n.body}</div>
                      )}
                      <div className="text-[10px] font-mono mt-1" style={{ color: "var(--text-dim)" }}>{relTime(n.created_at)}</div>
                    </div>
                  </div>
                );
                return (
                  <div key={n.id} className="relative">
                    {n.link ? (
                      <Link to={n.link} onClick={() => handleClick(n)} data-testid={`notification-${n.id}`} className="block">{inner}</Link>
                    ) : (
                      <button onClick={() => handleClick(n)} className="block w-full text-left" data-testid={`notification-${n.id}`}>{inner}</button>
                    )}
                    <button
                      onClick={() => dismiss(n)}
                      className="absolute top-3 right-3 p-1 rounded-lg transition hover:bg-white/10"
                      style={{ color: "var(--text-dim)" }}
                      title="Clear this notification"
                      data-testid={`dismiss-notification-${n.id}`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
